import React, { useState, useEffect } from 'react';
import { auditApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import { formatDistanceToNow } from 'date-fns';

const EVENT_CONFIG = {
  CAMERA_OFFLINE: { color: 'var(--accent-red)', icon: '⚠', label: 'Camera Offline' },
  CAMERA_ONLINE: { color: 'var(--accent-green)', icon: '✓', label: 'Camera Online' },
  MOTION_DETECTED: { color: 'var(--accent-amber)', icon: '◉', label: 'Motion Detected' },
  TAMPER_DETECTED: { color: 'var(--accent-red)', icon: '⊗', label: 'Tamper Alert' },
  CONNECTION_LOST: { color: 'var(--accent-red)', icon: '✕', label: 'Connection Lost' },
  default: { color: 'var(--text-muted)', icon: '·', label: 'Event' },
};

export default function EventsPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCamera, setFilterCamera] = useState('');
  const { cameras, addToast } = useAppStore();

  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, [filterCamera]);

  async function load() {
    try {
      const res = await auditApi.getEvents(filterCamera || null, 500);
      setEvents(res.data);
    } catch {
      addToast({ type: 'error', message: 'Failed to load events' });
    } finally {
      setLoading(false);
    }
  }

  const onlineCount = events.filter(e => e.type === 'CAMERA_ONLINE').length;
  const offlineCount = events.filter(e => e.type === 'CAMERA_OFFLINE').length;
  const motionCount = events.filter(e => e.type === 'MOTION_DETECTED').length;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '16px', gap: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--accent-cyan)', textTransform: 'uppercase' }}>
          Event Monitor
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            className="input-field select-field"
            style={{ width: '180px', height: '34px', fontSize: '12px', padding: '4px 28px 4px 10px', lineHeight: 'normal' }}
            value={filterCamera}
            onChange={e => setFilterCamera(e.target.value)}
          >
            <option value="">All Cameras</option>
            {cameras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={load}>Refresh</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
        {[
          { label: 'Online Events', value: onlineCount, color: 'var(--accent-green)' },
          { label: 'Offline Events', value: offlineCount, color: 'var(--accent-red)' },
          { label: 'Motion Events', value: motionCount, color: 'var(--accent-amber)' },
          { label: 'Total Events', value: events.length, color: 'var(--text-secondary)' },
        ].map(stat => (
          <div key={stat.label} style={{
            flex: 1,
            padding: '12px 16px',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-dim)',
          }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: 700, color: stat.color, lineHeight: 1 }}>{stat.value}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Event feed */}
      <div style={{
        flex: 1,
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-dim)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '10px 16px',
          background: 'var(--bg-elevated)',
          borderBottom: '1px solid var(--border-dim)',
          fontFamily: 'var(--font-display)',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span className="status-dot online animate-pulse" />
          Live Event Feed
        </div>

        <div className="scrollable" style={{ flex: 1 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>Loading events...</div>
          )}
          {!loading && events.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '14px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>No Events</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px' }}>Events are generated automatically when cameras change status</div>
            </div>
          )}
          {events.map((event, i) => {
            const cfg = EVENT_CONFIG[event.type] || EVENT_CONFIG.default;
            const cam = cameras.find(c => c.id === event.cameraId);
            return (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '12px 16px',
                borderBottom: '1px solid var(--border-dim)',
                transition: 'background 0.1s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  width: '28px', height: '28px',
                  borderRadius: '50%',
                  background: `${cfg.color}15`,
                  border: `1px solid ${cfg.color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px',
                  color: cfg.color,
                  flexShrink: 0,
                  marginTop: '2px',
                }}>
                  {cfg.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '13px', fontWeight: 700, color: cfg.color, letterSpacing: '0.04em' }}>
                      {cfg.label}
                    </span>
                    {cam && (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {cam.name}
                      </span>
                    )}
                  </div>
                  {event.description && (
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {event.description}
                    </div>
                  )}
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', flexShrink: 0, textAlign: 'right' }}>
                  <div>{new Date(event.timestamp).toLocaleTimeString()}</div>
                  <div style={{ fontSize: '9px', opacity: 0.6 }}>
                    {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
